'use client';

import React from 'react';
import classNames from 'classnames';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Author } from '@benzinga/article-manager';
import { AuthorSocialMedia, AuthorSocialMediaLinksI } from './AuthorSocialMedia';
import { authorTopicHref, normalizeAuthorTopics } from '../utils/authorTopics';
import { FollowAuthorButton } from './FollowAuthorButton';
import styles from './AuthorInfo.module.scss';

interface AuthorInfoProps {
  author: Author;
}

export const AuthorInfo: React.FC<AuthorInfoProps> = ({ author }) => {
  const topics = normalizeAuthorTopics(author.knowsAbout);

  const social: AuthorSocialMediaLinksI = {
    facebook: author.facebook,
    instagram: author.instagram,
    linkedin: author.linkedin,
    threads: author.threads,
    tiktok: author.tiktok,
    twitter: author.twitter,
    uid: author.uid,
  };

  return (
    <div className={classNames(styles.wrapper, 'author-info')}>
      <div className="grid grid-cols-1 gap-5">
        <div className="flex sm:items-center gap-3">
          <div className="avatar">
            <img alt={author.name} className="w-full h-full object-cover object-top rounded" src={author.picture} />
          </div>
          <div className="author-content">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h2 className="text-3xl">{author.name}</h2>
                <p className="mb-0 text">{author.byLine}</p>
                <p className="mb-2 text font-xs">{author.byLineSecondary}</p>
                <div className="flex gap-3">
                  <FollowAuthorButton authorName={author.name} authorUid={author.uid} variant="large" />
                  <AuthorSocialMedia social={social} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {author.about && (
          <div>
            <h3 className="heading">About {author.name}</h3>
            <div className="flex flex-col gap-2 mb-4">
              <div className="text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHTML(author.about) }} />
            </div>
          </div>
        )}
        {topics.length > 0 && (
          <div className={classNames(styles.expertise, 'author-expertise')}>
            <h3 className="heading" id="author-expertise-heading">
              Areas of Expertise
            </h3>
            <ul aria-labelledby="author-expertise-heading" className={styles.expertiseList}>
              {topics.map(topic => (
                <li key={topic}>
                  <a className={styles.expertiseLink} href={authorTopicHref(topic)}>
                    {topic}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorInfo;
